<template>
  <div>
    <q-toolbar v-if="quotes.length || isTemporaryResult" color="white" text-color="grey-9" class="top-page-toolbar">
      <q-toolbar-title class="q-body-2">
        <router-link class="text-grey-10 no-underline" :to="{ name: 'quotes', params: { teamId: activeTeam.id } }">
          {{ $tc('quote', 2) }}
          </router-link> / <span class="text-weight-bold">{{ $t('overview') }}</span>
      </q-toolbar-title>

      <q-btn
        @click="handleSetViewType('list')"
        icon="list"
        :color="type === 'list' ? 'grey-9' : 'grey-5'"
        round
        dense
        flat
      />
      <q-btn
        @click="handleSetViewType('grid')"
        icon="view_module"
        :color="type === 'grid' ? 'grey-9' : 'grey-5'"
        round
        dense
        flat
      />

      <div class="vr q-mx-sm" style="height: 50%;"/>

      <q-btn :label="$t('filter')" icon="filter_list" flat>
        <q-chip v-if="filterCount > 0" color="primary" floating>{{ filterCount }}</q-chip>
        <q-popover>
          <q-list separator link>
            <q-item>
              <q-item-side icon="local_offer"/>
              <q-item-main>
                <q-select
                  v-model="filter.status"
                  :options="quoteStatuses"
                  :placeholder="$t('quote_status')"
                  multiple
                  hide-underline
                  clearable
                />
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-side icon="calendar_today"/>
              <q-item-main>
                <q-datetime
                  :value="filter.startDate"
                  @change="val => { filter.startDate = val }"
                  type="date"
                  class="q-mr-lg"
                  :placeholder="$t('quote_filter_start_date')"
                  :format="$t('date_format')"
                  hide-underline
                  clearable
                />
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-side icon="calendar_today"/>
              <q-item-main>
                <q-datetime
                  :value="filter.endDate"
                  @change="val => { filter.endDate = val }"
                  type="date"
                  :placeholder="$t('quote_filter_end_date')"
                  :format="$t('date_format')"
                  hide-underline
                  clearable
                />
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-side icon="person" />
              <q-item-main>
                <q-select
                  v-model="filter.userId"
                  :options="userIdFilterOptions"
                  :placeholder="$t('invoice_filter_user_id')"
                  hide-underline
                  clearable
                />
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-side icon="contacts" />
              <q-item-main>
                <multi-customer-select
                  v-model="filter.customer"
                  class="q-ma-none"
                />
              </q-item-main>
            </q-item>
          </q-list>
        </q-popover>
      </q-btn>

      <div class="vr q-mx-sm" style="height: 50%;"/>

      <template>
        <q-btn :label="$t('export')" flat size="md" @click.native="handleExport"/>

        <q-btn icon="more_vert" class="invoice-toolbar__actions" color="grey-9" flat round dense>
          <q-popover>
            <q-list link class="no-border">
              <q-item
                :disabled="selectedQuotes.length !== 1 || selectedQuotes[0].status.value !== 'draft'"
                @click.native="callIfNotDisabled($event, handleEdit)"
                v-close-overlay
                v-is-user
              >
                <q-item-side icon="edit"/>
                <q-item-main :label="$t('edit')"/>
              </q-item>

              <q-item
                :disabled="!selectedQuotes.length"
                @click.native="callIfNotDisabled($event, handleRemove)"
                v-close-overlay
                v-is-user
              >
                <q-item-side icon="delete"/>
                <q-item-main :label="$t('remove')"/>
              </q-item>

              <q-item
                :disabled="selectedQuotes.length <= 1"
                v-close-overlay
                @click.native="callIfNotDisabled($event, handleExportSelected)"
              >
                <q-item-side icon="cloud_download"/>
                <q-item-main :label="$t('export_selected')"/>
              </q-item>

              <q-item
                :disabled="selectedQuotes.length !== 1"
                v-close-overlay
                @click.native="callIfNotDisabled($event, handlePdfDownload)"
              >
                <q-item-side icon="cloud_download"/>
                <q-item-main :label="$t('download_pdf')"/>
              </q-item>

              <q-item
                :disabled="selectedQuotes.length !== 1"
                v-close-overlay
                v-is-user
                @click.native="callIfNotDisabled($event, handleSend)"
              >
                <q-item-side icon="email"/>
                <q-item-main :label="$t('send_as_mail')"/>
              </q-item>
            </q-list>
          </q-popover>
        </q-btn>
      </template>
    </q-toolbar>

    <div class="layout-padding">
      <template v-if="quotes.length || isTemporaryResult">
        <template v-if="type === 'list'">
          <div class="round-borders no-scroll">
            <q-table
              :data="mappedQuotes"
              :columns="columns"
              selection="multiple"
              :selected.sync="selectedQuotes"
              :rows-per-page-options="rowsPerPageOptions"
              row-key="id"
              :pagination.sync="pagination"
              :loading="!quotesLoaded"
              @request="handleRequest"
              table-class="invoice-table"
              class="bg-white no-shadow no-border-radius"
              binary-state-sort
              grid
            >
              <q-td slot="body-cell-quote_number" slot-scope="props" :props="props">
                <router-link
                  v-if="props.row.status.value !== 'draft'"
                  :to="{ name: 'quote',  params: { quote: props.row.id, teamId: activeTeam.id } }"
                  class="router-link"
                >
                  {{ props.row.quote_number }}
                </router-link>

                <router-link
                  v-else
                  :to="{ name: 'edit-quote',  params: { quote: props.row.id, teamId: activeTeam.id } }"
                  class="router-link"
                >
                  {{ $t('draft') }}
                </router-link>
              </q-td>

              <q-td slot="body-cell-created_at" slot-scope="props" :props="props">
                <span>
                  {{ props.row.created_at_text }}

                  <q-tooltip anchor="top middle" self="bottom middle" :delay="300" :offset="[0, 5]">
                    {{ props.row.created_at_date }}
                  </q-tooltip>
                </span>
              </q-td>

              <q-td slot="body-cell-status" slot-scope="props" :props="props">
                <status-chip
                  :status="props.row.status.value"
                  :color="props.row.status.color"
                  :clickable="user.isUserOfActiveTeam()"
                  :committed="props.row.committed_at !== null"
                  @cancel="handleCancelQuote(props.row.id)"
                  @accepted="handleMarkQuoteAsAccepted(props.row.id)"
                  @open="handleMarkQuoteAsOpen(props.row.id)"
                  small
                />
              </q-td>

              <q-td slot="body-cell-actions" slot-scope="props" :props="props">
                <q-btn icon="more_vert" color="grey-7" class="invoice-row__actions" flat round dense>
                  <q-popover>
                    <q-list link class="no-border">
                      <q-item
                        v-if="props.row.status.value !== 'draft'"
                        v-close-overlay
                        @click.native="$router.push({ name: 'quote', params: { team: activeTeam.id, quote: props.row.id } })"
                      >
                        <q-item-side icon="visibility"/>
                        <q-item-main :label="$t('display')"/>
                      </q-item>

                      <q-item
                        v-if="props.row.committed_at === null"
                        v-close-overlay
                        @click.native="$router.push({ name: 'edit-quote', params: { team: activeTeam.id, quote: props.row.id } })"
                      >
                        <q-item-side icon="edit"/>
                        <q-item-main :label="$t('edit')"/>
                      </q-item>

                      <q-item
                        v-if="props.row.status.value !== 'draft'"
                        v-close-overlay
                        @click.native="handlePdfDownload(props.row)"
                      >
                        <q-item-side icon="cloud_download"/>
                        <q-item-main :label="$t('download_pdf')"/>
                      </q-item>

                      <q-item
                        v-if="props.row.status.value !== 'draft'"
                        v-close-overlay
                        @click.native="$router.push({
                          name: 'create-invoice',
                          params: { team: activeTeam.id  },
                          query: { quote: props.row.id }
                          })"
                      >
                        <q-item-side icon="receipt"/>
                        <q-item-main :label="$t('create_invoice')"/>
                      </q-item>

                      <q-item
                        v-if="props.row.status.value !== 'draft'"
                        v-close-overlay
                        v-is-user
                        @click.native="handleSend(props.row)"
                      >
                        <q-item-side icon="email"/>
                        <q-item-main :label="$t('send_as_mail')"/>
                      </q-item>

                      <q-item
                        v-close-overlay
                        v-is-user
                        @click.native="handleCopy(props.row)"
                      >
                        <q-item-side icon="file_copy" />
                        <q-item-main :label="$t('duplicate')" />
                      </q-item>

                      <q-item
                        v-if="props.row.status.value === 'draft'"
                        v-close-overlay
                        v-is-user
                        @click.native="handleRemove(props.row)"
                      >
                        <q-item-side icon="delete"/>
                        <q-item-main :label="$t('delete')"/>
                      </q-item>

                      <template v-if="props.row.committed_at === null && props.row.status.value !== 'draft'">
                        <q-item-separator/>

                        <q-item
                          v-close-overlay
                          v-is-user
                          @click.native="handleCommit(props.row)"
                        >
                          <q-item-side icon="lock"/>
                          <q-item-main :label="$t('commit')"/>
                        </q-item>
                      </template>
                    </q-list>
                  </q-popover>
                </q-btn>
              </q-td>
            </q-table>
          </div>
        </template>

        <template v-if="type === 'grid' && quotes.length">
          <q-infinite-scroll :handler="handleFetchNextChunk">
            <div class="row gutter-sm q-mt-sm">
              <div v-for="quote in quotes" :key="quote.id" class="col-xs-12 col-sm-6 col-md-4 col-xl-3">
                <quote-card :quote="quote" @select="handleSelect(quote, $event)"/>
              </div>
            </div>
          </q-infinite-scroll>
        </template>
      </template>

      <template v-if="showEmptyState">
        <div class="row justify-center items-center col-grow q-pt-lg">
          <div class="text-center">
            <img src="~assets/emptystates/invoices_empty.svg"/>

            <h6 class="q-my-md text-faded">{{ $t('quotes_empty') }}</h6>

            <q-btn @click="handleCreateQuote" color="primary" class="q-mt-lg" outline>
              {{ $t('create_quote') }}
            </q-btn>
          </div>
        </div>
      </template>
    </div>

    <q-page-sticky position="bottom-right" :offset="[18, 18]">
      <q-btn
        @click="$router.push({ name: 'create-quote' })"
        color="primary"
        class="add-invoice__button"
        round
        fab
        v-is-user
      >
        <q-icon name="add"/>
      </q-btn>
    </q-page-sticky>

    <quote-export-modal ref="quoteExportModal"/>
    <quote-send-modal ref="quoteSendModal"/>
    <success-modal :title="$t('export_successful_title')" :body="$t('export_successful_body')" ref="successModal"/>
  </div>
</template>

<script>
  import { defaultColumns, defaultMapFunction } from 'components/Quotes/Table/defaults'
  import { mapActions, mapGetters } from 'vuex'
  import { quotes, tables } from 'src/config'
  import FileSaver from 'file-saver'
  import MultiCustomerSelect from 'components/Common/Inputs/MultiCustomerSelect.vue'
  import QuoteCard from 'components/Quotes/QuoteCard.vue'
  import { QuoteClient } from 'src/api/Quotes'
  import QuoteExportModal from 'components/Quotes/QuoteExportModal.vue'
  import QuoteSendModal from 'components/Quotes/QuoteSendModal.vue'
  import StatusChip from 'components/Quotes/StatusChip.vue'
  import SuccessModal from 'components/Common/Modals/SuccessModal.vue'
  import { createErrorFromResponse } from 'src/utils/error'
  import { date } from 'quasar'
  import i18n from 'src/i18n/i18n'
  import { isEqual } from 'lodash'
  import parseDate from 'date-fns/parse'
  import store from 'src/store'

  const DEFAULT_PAGINATION = {
    page: 1,
    rowsNumber: 0,
    rowsPerPage: 15,
    descending: true,
    sortBy: 'created_at'
  }

  export default {
    data () {
      return {
        columns: defaultColumns,
        selectedQuotes: [],
        pagination: DEFAULT_PAGINATION,
        filter: {
          status: [],
          customer: [],
          startDate: null,
          endDate: null,
          userId: null
        },
        rowsPerPageOptions: tables.default_rows_per_page_options
      }
    },

    computed: {
      ...mapGetters(['quotes', 'quotesMeta', 'quotesLoaded', 'activeTeam', 'invoiceDisplayType', 'user']),

      quoteStatuses () {
        return quotes.statuses.filter(status => !['paid_cash_discount'].includes(status)).map(status => ({
          label: this.$t(status),
          value: status
        }))
      },

      userIdFilterOptions () {
        return this.activeTeam.members.map((member) => ({
          label: [member.first_name, member.last_name].join(' ').trim(),
          value: member.id
        }))
      },

      filterForRequest () {
        let filter = []

        if (this.filter.status.length > 0) {
          filter.push(['status', '=', this.filter.status])
        }

        if (this.filter.customer.length > 0) {
          const filterValue = `{${this.filter.customer.map((customer) => customer.id).join(';')}}`
          filter.push(['customer_id', '=', filterValue])
        }

        if (this.filter.startDate !== null) {
          filter.push(['start_date', '=', date.formatDate(parseDate(this.filter.startDate), 'YYYY-MM-DD')])
        }

        if (this.filter.endDate !== null) {
          filter.push(['end_date', '=', date.formatDate(parseDate(this.filter.endDate), 'YYYY-MM-DD')])
        }

        if (this.filter.userId !== null) {
          filter.push(['user_id', '=', this.filter.userId])
        }

        return filter
      },

      setFilters () {
        return Object.keys(this.filter)
          .filter((key) => {
            if (Array.isArray(this.filter[key])) {
              return this.filter[key].length !== 0
            } else {
              return this.filter[key] !== null
            }
          })
      },

      filterCount () {
        return this.setFilters.length
      },

      type: {
        get () {
          return this.invoiceDisplayType
        },

        set (value) {
          this.setInvoiceDisplayType(value)
        }
      },

      mappedQuotes () {
        return defaultMapFunction(this.quotes)
      },

      showEmptyState () {
        return (this.quotes.length === 0 && !this.isTemporaryResult) || (this.type === 'grid' && this.quotes.length === 0)
      },

      isTemporaryResult () {
        return this.filterCount > 0
      }
    },

    methods: {
      ...mapActions([
        'listQuotes',
        'fetchNextQuotesChunk',
        'deleteQuotes',
        'setInvoiceDisplayType',
        'setQuoteStatus',
        'commitQuote'
      ]),

      handleCommit (quote) {
        this.$q.dialog({
          title: this.$t('confirm_commit_quote_title'),
          message: this.$t('confirm_commit_quote_body'),
          ok: this.$t('confirm'),
          cancel: this.$t('abort')
        })
          .then(() => {
            this.commitQuote(quote.id)
              .catch(createErrorFromResponse)
          })
          .catch(() => {})
      },

      handleRequest ({ pagination }) {
        this.listQuotes({ pagination: {
          ...pagination,
          filterBy: this.filterForRequest
        } })
          .then(({ meta }) => {
            this.pagination = pagination
            this.pagination.rowsNumber = meta.pagination.total
          })
      },

      handleFetchNextChunk (index, done) {
        this.fetchNextQuotesChunk({
          done: done,
          pagination: {
            ...this.pagination,
            filterBy: this.filterForRequest
          }
        })
      },

      handleCopy (quote) {
        this.$router.push({
          name: 'create-quote',
          params: { teamId: this.activeTeam.id },
          query: { copy: quote.id }
        })
      },

      handleRemove (quote) {
        const subjects = quote ? [quote] : this.selectedQuotes

        this.$q.dialog({
          title: this.$tc('confirm_checked_quotes_delete_title', 1),
          message: this.$tc('confirm_checked_quotes_delete_body', 1),
          ok: this.$t('confirm'),
          cancel: this.$t('abort')
        })
          .then(() => this.deleteQuotes(subjects))
          .then(({ meta }) => {
            this.pagination.rowsNumber = meta.pagination.total
          })
          .then(() => {
            this.selectedQuotes = []
          })
          // no-op
          .catch(() => {})
      },

      handleCreateQuote () {
        this.$router.push({ name: 'create-quote' })
      },

      handleEdit () {
        this.$router.push({
          name: 'edit-quote',
          params: { team: this.activeTeam.id, quote: this.selectedQuotes[0].id }
        })
      },

      handleExport () {
        this.$refs.quoteExportModal.open()
      },

      handleExportSelected () {
        new QuoteClient()
          .export({
            id: this.selectedQuotes.map(quote => quote.id)
          })
          .then(() => {
            this.$refs.successModal.open()
          })
          .catch(error => {
            createErrorFromResponse(error)
          })
      },

      handlePdfDownload (quote = null) {
        const subject = quote || this.selectedQuotes[0]

        if (subject.status.value === 'draft') {
          this.$q.notify({ message: this.$t('draft_pdf_deny'), type: 'warning' })
          return
        }

        new QuoteClient()
          .pdf(subject.id)
          .then(response => {
            const blob = new Blob([response.data], { type: 'application/pdf' })
            FileSaver.saveAs(blob, i18n.tc('quote', 1) + '_' + subject.quote_number + '.pdf')
          })
      },

      handleSend (quote = null) {
        const subject = quote || this.selectedQuotes[0]
        this.$refs.quoteSendModal.open(subject)
      },

      handleSetViewType (type) {
        if (this.type === type) {
          return
        }

        this.selectedQuotes = []

        this.listQuotes()
          .then(({ meta }) => {
            this.pagination.rowsNumber = meta.pagination.total
          })
          .then(() => {
            this.type = type
          })
      },

      handleSelect (selectedInvoice, selected) {
        if (selected) {
          return this.selectedQuotes.push(selectedInvoice)
        }

        const index = this.selectedQuotes.findIndex(quote => quote.id === selectedInvoice.id)
        this.selectedQuotes.splice(index, 1)
      },

      handleCancelQuote (quoteId) {
        this.setQuoteStatus({ quoteId, status: 'canceled' })
          .catch(createErrorFromResponse)
      },

      handleMarkQuoteAsAccepted (quoteId) {
        this.setQuoteStatus({ quoteId, status: 'accepted' })
          .catch(createErrorFromResponse)
      },

      handleMarkQuoteAsOpen (quoteId) {
        this.setQuoteStatus({ quoteId, status: 'open' })
          .catch(createErrorFromResponse)
      },

      /**
       *  We have to use @click.native in lists due to a bug in our quasar version.
       *  Sadly @disabled does not work for these kind of events so we have to check the
       *  disabled state ourselves.
       */
      callIfNotDisabled (event, callback) {
        if (!event.target.parentNode.parentNode.attributes.disabled) {
          callback()
        }
      },

      updateRouteForPagination () {
        const pagination = JSON.stringify(this.pagination)
        return this.$router.replace({ query: { ...this.$route.query, p: pagination } })
      },

      parsePaginationFromQuery () {
        if (Object.prototype.hasOwnProperty.call(this.$route.query, 'p')) {
          const {
            page,
            descending,
            sortBy
          } = JSON.parse(this.$route.query.p)

          this.pagination.page = page
          this.pagination.descending = descending
          this.pagination.sortBy = sortBy
        }

        this.handleRequest({ pagination: this.pagination })
      }
    },

    watch: {
      filter: {
        handler: function () {
          this.handleRequest({ pagination: this.pagination })
        },
        deep: true
      },

      pagination: {
        // Check if pagination has change while omitting rowsNumber to prevent immediate change after first call.
        handler: function ({ rowsNumber, rowsPerPage, ...newPagination }) {
          const { rowsNumber: notUsed, rowsPerPage: notUsed2, ...defaultPagination } = DEFAULT_PAGINATION

          if (!isEqual(newPagination, defaultPagination)) {
            this.updateRouteForPagination()
          }
        },
        deep: true
      }
    },

    beforeRouteEnter (to, from, next) {
      store.dispatch('listQuotes')
        .then(({ meta }) => {
          next(vm => {
            vm.pagination.rowsNumber = meta.pagination.total
            vm.parsePaginationFromQuery()
          })
        })
    },

    components: {
      QuoteExportModal,
      QuoteSendModal,
      StatusChip,
      SuccessModal,
      QuoteCard,
      MultiCustomerSelect
    }
  }
</script>
